/**
 * Procedural map generation (GDD §14).
 *
 * The map is an odd-r rectangle with an even number of rows, so the 180° rotation
 * `index → size − 1 − index` is an exact isometry. Everything is generated for the northern half
 * (team 0) and mirrored, which makes the map point-symmetric and therefore fair by construction:
 * every distance, feature and resource team 0 has, team 1 has rotated.
 *
 * Steps: noise-driven terrain & biomes → a meandering river across the middle band (a complete
 * barrier) → keeps → river crossings (a bridge pair, plus a ford pair when there is room) →
 * landmarks on each bank → roads (keep→villages, keep→crossing→enemy keep) → names.
 * A failed check (river not a barrier, no crossing site, keeps disconnected) retries with a new
 * attempt seed; the result is a pure function of the seed and team sizes.
 */
import { BALANCE } from '../balance';
import { hexDistance, hexLine, pixelToHex, type Axial } from '../hex';
import { Rng, hashString } from '../rng';
import type { Biome, Feature, GameMap, MapHex, TeamId, Terrain } from '../types';
import { coordOf, indexOf, neighbourIndices, rotateIndex, topology, type Topology } from './grid';
import { names } from './names';
import { findPathIndices, isPassable } from './pathfinding';

export interface GeneratedMap {
  map: GameMap;
  keeps: [Axial, Axial];
  /** Water hexes where roads cross (bridges and fords). */
  crossings: Axial[];
  /** Generation-time road / bridge hex indices (the static map clients receive). */
  roads: number[];
  bridges: number[];
  /** Names already used on the map, so later names (relics, armies) stay unique. */
  usedNames: string[];
}

export interface FeatureCounts {
  villagesPerSide: number;
  leyNodesPerSide: number;
  shrinesPerSide: number;
  ruinsPerSide: number;
}

const SQRT3_2 = Math.sqrt(3) / 2;

/** Map size for a total number of player slots (GDD: ~60 hexes per player plus a middle band). */
export function mapDimensions(totalPlayers: number): { width: number; height: number } {
  const { hexesPerPlayer, middleBandFactor, aspect, minWidth, minHeight } = BALANCE.map;
  const area = Math.max(2, totalPlayers) * hexesPerPlayer * middleBandFactor;
  const width = Math.max(minWidth, Math.round(Math.sqrt(area * aspect)));
  let height = Math.max(minHeight, Math.ceil(area / width));
  if (height % 2 === 1) height += 1;
  return { width, height };
}

/** Landmark counts per side (GDD §14): villages ~3/player, ley & shrines ~1/player, ruins 2 + players/2. */
export function featureCounts(totalPlayers: number): FeatureCounts {
  const p = Math.max(2, totalPlayers);
  return {
    villagesPerSide: Math.round((3 * p) / 2),
    leyNodesPerSide: Math.max(1, Math.round(p / 2)),
    shrinesPerSide: Math.max(1, Math.round(p / 2)),
    ruinsPerSide: Math.max(1, Math.round((2 + p / 2) / 2)),
  };
}

export function generateMap(seed: number, teamSizes: readonly [number, number]): GeneratedMap {
  const total = teamSizes[0] + teamSizes[1];
  let lastError = 'unknown';
  for (let attempt = 0; attempt < BALANCE.map.maxAttempts; attempt++) {
    const rng = new Rng(hashString(`${seed}:${attempt}`));
    const result = tryGenerate(seed, rng, total);
    if (typeof result !== 'string') return result;
    lastError = result;
  }
  throw new Error(`Map generation failed for seed ${seed}: ${lastError}`);
}

// ---------------------------------------------------------------------------------------------

interface Builder {
  map: GameMap;
  topo: Topology;
  rng: Rng;
  size: number;
  /** Indices in the northern (team 0) half: index < size / 2. */
  half: number;
}

function pixelOf(topo: Topology, i: number): { x: number; y: number } {
  const col = i % topo.width;
  const row = Math.floor(i / topo.width);
  return { x: col + 0.5 * (row & 1), y: row * SQRT3_2 };
}

/** Smooth value noise in [0, 1], sampled at hex centres. */
function valueNoise(rng: Rng, topo: Topology, cell: number): Float64Array {
  const gw = Math.ceil(topo.width / cell) + 3;
  const gh = Math.ceil((topo.height * SQRT3_2) / cell) + 3;
  const lattice = Array.from({ length: gw * gh }, () => rng.next());
  const at = (x: number, y: number): number => lattice[y * gw + x] ?? 0;
  const smooth = (t: number): number => t * t * (3 - 2 * t);
  const out = new Float64Array(topo.size);
  for (let i = 0; i < topo.size; i++) {
    const p = pixelOf(topo, i);
    const gx = p.x / cell + 1;
    const gy = p.y / cell + 1;
    const x0 = Math.floor(gx);
    const y0 = Math.floor(gy);
    const tx = smooth(gx - x0);
    const ty = smooth(gy - y0);
    const top = at(x0, y0) * (1 - tx) + at(x0 + 1, y0) * tx;
    const bottom = at(x0, y0 + 1) * (1 - tx) + at(x0 + 1, y0 + 1) * tx;
    out[i] = top * (1 - ty) + bottom * ty;
  }
  return out;
}

function layeredNoise(rng: Rng, topo: Topology, cell: number): Float64Array {
  const a = valueNoise(rng, topo, cell);
  const b = valueNoise(rng, topo, cell / 2);
  const out = new Float64Array(topo.size);
  for (let i = 0; i < topo.size; i++) out[i] = (a[i] as number) * 0.7 + (b[i] as number) * 0.3;
  return out;
}

/** Value at the given quantile (0–1) of the northern half of a field. */
function quantile(field: Float64Array, half: number, q: number): number {
  const values = Array.from(field.subarray(0, half)).sort((x, y) => x - y);
  return values[Math.min(values.length - 1, Math.floor(q * values.length))] ?? 1;
}

function setMirrored(b: Builder, i: number, update: (hex: MapHex) => void): void {
  for (const idx of [i, rotateIndex(b.map, i)]) {
    const hex = b.map.hexes[idx];
    if (hex) update(hex);
  }
}

function hexOf(b: Builder, i: number): MapHex {
  const hex = b.map.hexes[i];
  if (!hex) throw new Error(`hex ${i} out of range`);
  return hex;
}

function paintTerrain(b: Builder): void {
  const { topo, rng, half } = b;
  const elevation = layeredNoise(rng, topo, 4.5);
  const moisture = layeredNoise(rng, topo, 3.5);
  const aridity = valueNoise(rng, topo, 7);
  const mountainAt = quantile(elevation, half, 0.92);
  const hillsAt = quantile(elevation, half, 0.78);
  const forestAt = quantile(moisture, half, 0.7);
  const marshAt = quantile(moisture, half, 0.93);
  const aridAt = quantile(aridity, half, 0.72);
  for (let i = 0; i < half; i++) {
    const e = elevation[i] as number;
    const m = moisture[i] as number;
    const biome: Biome = (aridity[i] as number) >= aridAt ? 'arid' : 'temperate';
    let terrain: Terrain = 'plains';
    if (e >= mountainAt) terrain = 'mountain';
    else if (e >= hillsAt) terrain = 'hills';
    else if (biome === 'temperate' && m >= marshAt) terrain = 'marsh';
    else if (m >= forestAt && (biome === 'temperate' || m >= marshAt)) terrain = 'forest';
    setMirrored(b, i, (hex) => {
      hex.terrain = terrain;
      hex.biome = biome;
    });
  }
}

/**
 * Rasterise a river y = cy + Σ aₖ·sin(kₖ·(x − cx)). The sine terms are odd about the map centre,
 * so the curve is itself point-symmetric; the union with the rotated raster removes rounding drift.
 */
function paintRiver(b: Builder): number[] {
  const { topo, rng, map } = b;
  const cx = (topo.width - 0.5) / 2;
  const cy = ((topo.height - 1) * SQRT3_2) / 2;
  const span = topo.width;
  const waves = [
    { a: rng.range(0.5, 1.3) * SQRT3_2, k: (2 * Math.PI) / (span * rng.range(0.7, 1.3)) },
    { a: rng.range(0.2, 0.6) * SQRT3_2, k: (2 * Math.PI) / (span * rng.range(0.3, 0.5)) },
  ];
  const water = new Set<number>();
  let prev: Axial | null = null;
  for (let x = -1.5; x <= topo.width + 1.5; x += 0.2) {
    const y = cy + waves.reduce((sum, w) => sum + w.a * Math.sin(w.k * (x - cx)), 0);
    const h = pixelToHex(x, y, 1);
    const segment = prev ? hexLine(prev, h) : [h];
    for (const s of segment) {
      const idx = indexOf(map, s);
      if (idx >= 0) water.add(idx);
    }
    prev = h;
  }
  for (const idx of [...water]) water.add(rotateIndex(map, idx));
  for (const idx of water) {
    const hex = hexOf(b, idx);
    hex.terrain = 'water';
  }
  return [...water].sort((x, y) => x - y);
}

/** Flood fill over land passable on foot (ignoring roads/crossings). */
function landReach(b: Builder, from: number): Uint8Array {
  const seen = new Uint8Array(b.size);
  const queue = [from];
  seen[from] = 1;
  while (queue.length > 0) {
    const cur = queue.pop() as number;
    for (const n of neighbourIndices(b.topo, cur)) {
      if (seen[n]) continue;
      const hex = hexOf(b, n);
      if (hex.terrain === 'water' || hex.terrain === 'mountain') continue;
      seen[n] = 1;
      queue.push(n);
    }
  }
  return seen;
}

function pickKeep(b: Builder): number {
  const { topo, rng } = b;
  const spread = Math.max(1, Math.floor(topo.width / 6));
  const col = Math.floor(topo.width / 2) - 1 + rng.int(-spread, spread);
  const row = 1;
  const keep = row * topo.width + col;
  // Clear the keep and its surroundings so the garrison has room to deploy.
  for (const i of [keep, ...neighbourIndices(topo, keep)]) {
    setMirrored(b, i, (hex) => {
      hex.terrain = 'plains';
    });
  }
  return keep;
}

interface Crossing {
  water: number;
  north: number;
  south: number;
}

/** Water hexes one hex thick with a reachable north-bank and south-bank neighbour. */
function crossingCandidates(b: Builder, bank0: Uint8Array, colMin: number, colMax: number): Crossing[] {
  const out: Crossing[] = [];
  for (let i = 0; i < b.size; i++) {
    const hex = hexOf(b, i);
    const col = i % b.topo.width;
    if (hex.terrain !== 'water' || col < colMin || col > colMax) continue;
    const ns = neighbourIndices(b.topo, i);
    const north = ns.find((n) => bank0[n] === 1);
    const south = ns.find((n) => bank0[rotateIndex(b.map, n)] === 1);
    if (north !== undefined && south !== undefined) out.push({ water: i, north, south });
  }
  return out;
}

/** Road-building cost: prefers open ground and existing roads; water only at designated crossings. */
function roadCost(b: Builder, crossings: Set<number>): (from: number, to: number) => number {
  const terrainWeight: Record<Terrain, number> = {
    plains: 1,
    forest: 1.8,
    hills: 2,
    marsh: 2.5,
    mountain: Infinity,
    water: Infinity,
  };
  return (_from, to) => {
    const hex = hexOf(b, to);
    if (hex.terrain === 'water') return crossings.has(to) ? 2 : Infinity;
    const w = terrainWeight[hex.terrain];
    return hex.road ? Math.min(w, 0.35) : w;
  };
}

function layRoad(b: Builder, path: number[]): void {
  for (const i of path) {
    setMirrored(b, i, (hex) => {
      hex.road = true;
    });
  }
}

/** Chooses `count` hexes from `candidates` keeping `spacing` from every placed feature (and mirrors). */
function placeSpaced(
  b: Builder,
  candidates: number[],
  count: number,
  taken: number[],
  spacing: number,
): number[] {
  const chosen: number[] = [];
  const pool = [...candidates];
  while (chosen.length < count && pool.length > 0) {
    const pickAt = b.rng.int(0, pool.length - 1);
    const i = pool[pickAt] as number;
    pool.splice(pickAt, 1);
    const c = coordOf(b.map, i);
    const mirror = coordOf(b.map, rotateIndex(b.map, i));
    if (hexDistance(c, mirror) < spacing) continue;
    const tooClose = taken.some((t) => {
      const tc = coordOf(b.map, t);
      return hexDistance(tc, c) < spacing || hexDistance(tc, mirror) < spacing;
    });
    if (tooClose) continue;
    chosen.push(i);
    taken.push(i, rotateIndex(b.map, i));
  }
  return chosen;
}

function tryGenerate(seed: number, rng: Rng, totalPlayers: number): GeneratedMap | string {
  const { width, height } = mapDimensions(totalPlayers);
  const hexes: MapHex[] = Array.from({ length: width * height }, () => ({
    terrain: 'plains',
    biome: 'temperate',
    road: false,
    bridge: false,
    ford: false,
    feature: null,
  }));
  const map: GameMap = { width, height, seed, hexes };
  const topo = topology(map);
  const b: Builder = { map, topo, rng, size: topo.size, half: topo.size / 2 };

  paintTerrain(b);
  const water = paintRiver(b);
  const keep0 = pickKeep(b);
  const keep1 = rotateIndex(map, keep0);

  const bank0 = landReach(b, keep0);
  if (bank0[keep1]) return 'river does not separate the teams';

  // Crossings: a bridge pair west/east of centre, plus a ford pair nearer the centre if room.
  const bridgeSites = crossingCandidates(b, bank0, Math.floor(width * 0.12), Math.ceil(width * 0.38));
  if (bridgeSites.length === 0) return 'no bridge site';
  const bridge = rng.pick(bridgeSites);
  const crossings: Crossing[] = [bridge];
  const fordSites = crossingCandidates(b, bank0, Math.ceil(width * 0.38) + 1, Math.floor(width / 2)).filter(
    (c) => {
      const pos = coordOf(map, c.water);
      return (
        hexDistance(pos, coordOf(map, rotateIndex(map, c.water))) >= 3 &&
        hexDistance(pos, coordOf(map, bridge.water)) >= 3
      );
    },
  );
  const ford = fordSites.length > 0 ? rng.pick(fordSites) : null;
  if (ford) crossings.push(ford);

  const crossingSet = new Set<number>();
  for (const c of crossings) {
    crossingSet.add(c.water);
    crossingSet.add(rotateIndex(map, c.water));
  }
  setMirrored(b, bridge.water, (hex) => {
    hex.bridge = true;
  });
  if (ford) {
    setMirrored(b, ford.water, (hex) => {
      hex.ford = true;
    });
  }

  // Landmarks on the north bank (mirrored onto the south bank).
  const counts = featureCounts(totalPlayers);
  const riverDist = distanceToSet(b, water);
  const keepCoord = coordOf(map, keep0);
  const land = (i: number): boolean =>
    i < b.half && bank0[i] === 1 && i !== keep0 && !crossingSet.has(i) && hexOf(b, i).terrain !== 'water';
  const allLand = Array.from({ length: b.half }, (_, i) => i).filter(land);
  const taken: number[] = [keep0, keep1];
  const byRiver = (maxDist: number): number[] =>
    allLand.filter((i) => (riverDist[i] as number) >= 1 && (riverDist[i] as number) <= maxDist);

  const shrines = placeSpaced(b, byRiver(3), counts.shrinesPerSide, taken, 3);
  const nearMiddle = [...allLand].sort((x, y) => (riverDist[x] as number) - (riverDist[y] as number));
  const leyPool = nearMiddle.slice(
    0,
    Math.max(counts.leyNodesPerSide * 4, Math.ceil(nearMiddle.length * 0.45)),
  );
  const leyNodes = placeSpaced(b, leyPool, counts.leyNodesPerSide, taken, 3);
  const ruinPool = allLand.filter((i) => (riverDist[i] as number) >= 2 && (riverDist[i] as number) <= 6);
  const ruins = placeSpaced(b, ruinPool, counts.ruinsPerSide, taken, 3);
  const villagePool = allLand.filter((i) => hexDistance(coordOf(map, i), keepCoord) >= 2);
  const villages = placeSpaced(b, villagePool, counts.villagesPerSide, taken, 2);
  if (shrines.length === 0 || ruins.length === 0 || villages.length < counts.villagesPerSide / 2) {
    return 'not enough room for landmarks';
  }

  // Roads: keep → crossing → enemy keep (mirrored gives the return road), keep → villages.
  const cost = roadCost(b, crossingSet);
  for (const c of crossings) {
    const toNorth = findPathIndices(topo, keep0, c.north, cost, 0.35);
    const toKeep = findPathIndices(topo, c.south, keep1, cost, 0.35);
    if (!toNorth || !toKeep) return 'crossing unreachable';
    layRoad(b, [keep0, ...toNorth, c.water, c.south, ...toKeep]);
  }
  for (const v of villages) {
    if (hexDistance(coordOf(map, v), keepCoord) > height / 2 + 2 || !rng.chance(0.8)) continue;
    const path = findPathIndices(topo, keep0, v, cost, 0.35);
    if (path) layRoad(b, path);
  }

  // Names and features.
  const used = new Set<string>();
  const nameRng = new Rng(hashString(`names:${seed}`));
  const teamKeeps: [number, number] = [keep0, keep1];
  for (const team of [0, 1] as TeamId[]) {
    hexOf(b, teamKeeps[team]).feature = { kind: 'keep', teamId: team, name: names.keep(nameRng, used) };
  }
  const placePair = (indices: number[], make: () => Feature): void => {
    for (const i of indices) {
      hexOf(b, i).feature = make();
      hexOf(b, rotateIndex(map, i)).feature = make();
    }
  };
  placePair(villages, () => ({ kind: 'village', name: names.village(nameRng, used), ownerTeam: null }));
  placePair(leyNodes, () => ({ kind: 'leyNode', name: names.leyNode(nameRng, used), ownerTeam: null }));
  placePair(shrines, () => ({ kind: 'shrine', name: names.shrine(nameRng, used) }));
  placePair(ruins, () => ({ kind: 'ruins', name: names.ruins(nameRng, used) }));

  const keeps: [Axial, Axial] = [coordOf(map, keep0), coordOf(map, keep1)];
  const problem = checkConnectivity(map, keeps);
  if (problem) return problem;

  const roads: number[] = [];
  const bridges: number[] = [];
  map.hexes.forEach((hex, i) => {
    if (hex.road) roads.push(i);
    if (hex.bridge) bridges.push(i);
  });
  return {
    map,
    keeps,
    crossings: [...crossingSet].sort((x, y) => x - y).map((i) => coordOf(map, i)),
    roads,
    bridges,
    usedNames: [...used],
  };
}

function distanceToSet(b: Builder, sources: number[]): Int32Array {
  const dist = new Int32Array(b.size).fill(1 << 30);
  const queue: number[] = [];
  for (const s of sources) {
    dist[s] = 0;
    queue.push(s);
  }
  for (let head = 0; head < queue.length; head++) {
    const cur = queue[head] as number;
    for (const n of neighbourIndices(b.topo, cur)) {
      if ((dist[n] as number) <= (dist[cur] as number) + 1) continue;
      dist[n] = (dist[cur] as number) + 1;
      queue.push(n);
    }
  }
  return dist;
}

/**
 * Connectivity check between the keeps on foot: returns an error string, or null when the keeps
 * are connected by at least two crossing-disjoint routes (blocking any single crossing hex still
 * leaves a route) and every landmark is reachable.
 */
export function checkConnectivity(map: GameMap, keeps: readonly [Axial, Axial]): string | null {
  const topo = topology(map);
  const start = indexOf(map, keeps[0]);
  const goal = indexOf(map, keeps[1]);
  const reach = (blocked: number): Uint8Array => {
    const seen = new Uint8Array(topo.size);
    const queue = [start];
    seen[start] = 1;
    while (queue.length > 0) {
      const cur = queue.pop() as number;
      for (const n of neighbourIndices(topo, cur)) {
        if (seen[n] || n === blocked || !isPassable(map.hexes[n])) continue;
        seen[n] = 1;
        queue.push(n);
      }
    }
    return seen;
  };
  const all = reach(-1);
  if (!all[goal]) return 'keeps are not connected';
  for (let i = 0; i < topo.size; i++) {
    if (map.hexes[i]?.feature && !all[i]) return `landmark at ${i} is unreachable`;
  }
  const crossings = map.hexes.flatMap((hex, i) => (hex.terrain === 'water' && isPassable(hex) ? [i] : []));
  if (crossings.length < 2) return 'fewer than two river crossings';
  for (const c of crossings) {
    if (!reach(c)[goal]) return `crossing ${c} is the only route`;
  }
  return null;
}
